<template>
  <v-flex xs12>
    <div class="section-title di-flex justify-space-between">
      Prescriptions
      <v-tooltip top>
        <v-btn
          small
          icon
          slot="activator"
          color="secondary"
          @click="dialog = true;"
          class="ma-0 shadow"
        >
          <v-icon small>mdi-plus</v-icon>
        </v-btn>

        <span>Add Prescriptions</span>
      </v-tooltip>
    </div>
    <v-layout row justify-center>
      <v-dialog v-model="dialog" persistent max-width="1200px">
        <v-card class="px-3">
          <v-card-title primary-title class="title">{{presTitle}}</v-card-title>
          <v-card-text class="pa-0">
            <v-form v-model="valid" ref="form" lazy-validation>
              <v-container grid-list-xs fluid>
                <v-layout row wrap>
                  <v-flex xs12>
                    <v-textarea rows="3" label="Diagnoses" v-model="newPres.diagnoses"></v-textarea>
                  </v-flex>

                  <v-flex xs12>
                    <div class="d-flex">
                      <v-autocomplete
                        :items="drugGroups"
                        single-line
                        hide-details
                        box
                        hide-selected
                        item-text="name"
                        item-value="id"
                        label="Search on drug groups"
                        dense
                        @change="addDrugGroup"
                        return-object
                      ></v-autocomplete>
                      <v-autocomplete
                        class="ml-2"
                        :items="drugs"
                        single-line
                        hide-details
                        box
                        hide-selected
                        item-text="name"
                        item-value="id"
                        label="Search on drug"
                        dense
                        @change="addDrug"
                        return-object
                      ></v-autocomplete>
                    </div>
                    <v-data-table :headers="headers" :items="newPres.drugs" hide-actions>
                      <template slot="items" slot-scope="props">
                        <td>{{ props.index + 1 }}</td>
                        <td>{{ props.item.name }}</td>
                        <td>
                          <input class="pa-1" v-model="props.item.route" />
                        </td>
                        <td>
                          <input class="pa-1" v-model="props.item.duration" />
                        </td>
                        <td>
                          <input class="pa-1" v-model="props.item.usage" />
                        </td>
                        <td>
                          <input class="pa-1" type="number" v-model="props.item.quantity" />
                        </td>
                        <td>
                          <input class="pa-1" v-model="props.item.type" />
                        </td>
                        <td>
                          <v-icon color="error" @click="removeDrug(props.item)">mdi-close</v-icon>
                        </td>
                      </template>
                    </v-data-table>
                  </v-flex>

                  <v-flex xs12 class="text-xs-right mt-3">
                    <v-btn flat @click="clean">Close</v-btn>

                    <v-btn
                      class="mx-0"
                      :loading="loading"
                      color="primary"
                      @click="submit"
                      :disabled="!valid"
                    >Save</v-btn>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-form>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-layout>
    <v-layout row wrap>
      <v-flex xs12>
        <v-data-table
          class="highlight-rows"
          item-key="id"
          :headers="headers3"
          :items="patient.prescriptions"
          disable-initial-sort
          hide-actions
        >
          <template slot="items" slot-scope="props">
            <tr @click="props.expanded = !props.expanded">
              <td>{{ props.item.doctor.name }}</td>
              <td>{{ props.item.created_at | moment('YYYY-MM-DD') }}</td>
              <td>{{props.item.total}}</td>
              <td class="text-capitalize" :class="props.item.status">{{props.item.status}}</td>
              <td>
                <v-tooltip top v-if="props.item.status === 'pending'">
                  <v-icon slot="activator" @click="deletePres(props.item)">mdi-delete-outline</v-icon>
                  <span>Delete</span>
                </v-tooltip>

                <v-tooltip top>
                  <v-icon slot="activator" @click="editPres(props.item)">mdi-pencil-outline</v-icon>
                  <span>Edit</span>
                </v-tooltip>

                <v-tooltip top>
                  <v-icon slot="activator" @click="print(props.item)">mdi-printer</v-icon>
                  <span>Print</span>
                </v-tooltip>
              </td>
            </tr>
          </template>

          <template v-slot:expand="{item}">
            <v-data-table
              class="sub-table"
              item-key="id"
              :headers="drugHeaders"
              :items="item.drugs"
              disable-initial-sort
              hide-actions
            >
              <template slot="items" slot-scope="props">
                <td>{{ props.item.name }}</td>
                <td>{{ props.item.route }}</td>
                <td>{{ props.item.duration }}</td>
                <td>{{ props.item.usage }}</td>
                <td>{{ props.item.type }}</td>
                <td>{{props.item.quantity}}</td>
                <td>{{props.item.price}}</td>
                <td>{{ props.item.price * props.item.quantity}}</td>
              </template>
            </v-data-table>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>
  </v-flex>
</template>

<script>
import { mapGetters } from "vuex";
export default {
  props: {
    patient: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      dialog: false,
      valid: true,
      requiredRule: v => !!v || "This field is required.",
      loading: false,
      newPres: {
        diagnoses: "",
        drugs: []
      },
      headers: [
        { text: "#", value: "id", sortable: false },
        { text: "Drug Name", value: "name", sortable: false },
        { text: "Route", value: "route", sortable: false },
        { text: "Duration", value: "duration", sortable: false },
        { text: "Usage", value: "usage", sortable: false },
        { text: "Quantity", value: "quantity", sortable: false, width: "50" },
        { text: "Type", value: "type", sortable: false },
        { text: "", value: "id", sortable: false, width: "50" }
      ],
      headers3: [
        { text: "Doctor Name", value: "doctor.name" },
        { text: "Date", value: "created_at" },
        { text: "Total (AFN)", value: "total" },
        { text: "Status", value: "status" },
        { text: "", value: "name", sortable: false }
      ],

      drugHeaders: [
        { text: "Drug Name", value: "name", sortable: false },
        { text: "Route", value: "route", sortable: false },
        { text: "Duration", value: "duration", sortable: false },
        { text: "Usage", value: "usage", sortable: false },
        { text: "Type", value: "type", sortable: false },
        { text: "Quantity", value: "quantity", sortable: false },
        { text: "Price (AFN)", value: "price", sortable: false },
        { text: "Total (AFN)", value: "name", sortable: false }
      ]
    };
  },
  computed: {
    presTitle() {
      return this.newPres.id ? "Edit Prescription" : "New Prescription";
    },
    ...mapGetters({
      drugs: "base/drugs",
      drugGroups: "base/drugGroups"
    }),
    user() {
      return this.$store.getters["auth/user"];
    }
  },
  methods: {
    print(item) {
      this.$store.commit("doctors/setPrescription", item);

      this.$router.push("/print/prescription");
    },
    removeDrug(drug) {
      this.newPres.drugs.splice(this.newPres.drugs.indexOf(drug), 1);
    },
    addDrugGroup(group) {
      for (const drug of group.drugs) {
        this.addDrug(drug);
      }
    },
    addDrug(drug) {
      this.newPres.drugs.push({
        id: drug.id,
        name: drug.name,
        route: drug.route,
        duration: drug.duration,
        usage: drug.usage,
        quantity: 1,
        type: drug.type,
        price: drug.price
      });
    },
    clean() {
      this.loading = false;
      this.dialog = false;
      this.newPres = { diagnoses: "", drugs: [] };
    },
    submit() {
      if (this.newPres.drugs.length === 0) return;

      const total = this.newPres.drugs.reduce((prev, curr) => {
        return (prev += curr.price * curr.quantity);
      }, 0);


      this.loading = true;
      let data = {
        patient_id: this.patient.id,
        doctor_id: this.user.id,
        diagnoses: this.newPres.diagnoses,
        drugs: this.newPres.drugs,
        total: total
      };

      if (this.newPres.id) data.id = this.newPres.id;

      this.axios
        .post(`/api/prescriptions`, data)
        .then(resp => {
          this.loading = false;
          this.$emit("refresh");
        })
        .finally(() => this.clean());
    },
    deletePres(pres) {
      this.$modal.show({
        message: `Are you sure, you want to delete the prescription?`,
        mode: "delete",
        onConfirm: () => {
          this.axios
            .delete(`/api/prescriptions/${pres.id}`)
            .then(resp => this.$emit("refresh"));
        }
      });
    },
    editPres(pres) {
      Object.assign(this.newPres,pres)
      this.dialog = true;
    }
  },
  mounted() {
    this.$store.dispatch("base/loadDrugs");
    this.$store.dispatch("base/loadDrugGroups");
  }
};
</script>

<style lang="scss" scoped>
</style>